<template>
  <div class="cv-rules">
    <div class="cv-rules__toolbar">
      <div class="cv-rules__filters">
        <label for="cv-rules-jurisdiction" class="mb-0 font-weight-bold">Jurisdiction</label>
        <b-form-select
          id="cv-rules-jurisdiction"
          v-model="jurisdiction"
          :options="jurisdictionOptions"
          size="sm"
          class="cv-rules__select"
        />
        <b-form-select
          id="cv-rules-type"
          v-model="typeFilter"
          :options="typeOptions"
          size="sm"
          class="cv-rules__select"
          aria-label="Rule type"
        />
        <b-form-select
          id="cv-rules-status"
          v-model="statusFilter"
          :options="statusOptions"
          size="sm"
          class="cv-rules__select"
          aria-label="Status"
        />
        <div class="cv-rules__switch">
          <base-switch id="cv-rules-hide-inactive" v-model="hideInactive" onText="" offText=""></base-switch>
          <label for="cv-rules-hide-inactive" class="cv-rules__switch-label mb-0">Hide inactive rules</label>
        </div>
      </div>
      <b-button v-if="canManage" size="sm" variant="primary" :disabled="busy" @click="openBuilder('create')">
        + Add rule
      </b-button>
    </div>

    <p class="text-muted small mb-2">{{ summary }}</p>

    <b-table
      :items="visibleRules"
      :fields="fields"
      :tbody-tr-class="rowClass"
      class="cv-rules__table"
      sort-icon-left
      small
      responsive
      show-empty
      empty-text="No rules match these filters."
    >
      <template #cell(type)="{ item }">
        <b-badge :variant="item.type === 'custom' ? 'info' : 'light'">
          {{ item.type === 'custom' ? 'Custom' : 'Default' }}
        </b-badge>
      </template>

      <template #cell(name)="{ item }">
        <span class="font-weight-bold">{{ item.name }}</span>
        <span v-if="item.version > 1" class="text-muted small">(version {{ item.version }})</span>
      </template>

      <template #cell(status)="{ item }">
        <template v-if="item.type === 'default'">
          <b-form-select
            v-if="canChangeDefaultSeverity(item)"
            :key="`${item.id}-${item.severity}-${severityResets}`"
            :value="item.severity"
            :options="defaultSeverityOptions"
            :disabled="busy"
            size="sm"
            class="cv-rules__severity"
            :aria-label="`Severity of ${item.name}`"
            @change="(value) => changeDefaultSeverity(item, value)"
          />
          <b-badge v-else :variant="severityVariant(item.severity)">{{ severityLabel(item.severity) }}</b-badge>
          <div v-if="isRaised(item)" class="text-muted small">
            Raised from {{ severityLabel(item.default_severity) }}
          </div>
        </template>
        <template v-else>
          <b-badge :variant="severityVariant(item.severity)">{{ severityLabel(item.severity) }}</b-badge>
          <b-badge :variant="item.active ? 'success' : 'secondary'" class="ml-1">
            {{ item.active ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
      </template>

      <template #cell(columns)="{ item }">{{ columnsText(item) }}</template>

      <template #cell(criteria)="{ item }">{{ criteriaOf(item) }}</template>

      <template #cell(scope)="{ item }">
        <div
          v-for="(line, index) in scopeLines(item)"
          :key="line"
          :class="{ 'text-nowrap': index === 0 }"
          :title="scopeTitle(item)"
        >
          {{ line }}
        </div>
      </template>

      <template #cell(actions)="{ item }">
        <div class="cv-rules__actions">
          <template v-if="item.type === 'custom' && canManage">
            <b-button
              v-if="item.active"
              size="sm"
              variant="outline-danger"
              :disabled="busy"
              @click="deactivate(item)"
            >
              Deactivate
            </b-button>
            <span v-else v-b-tooltip.hover :title="replacedBy(item) ? `Replaced by version ${replacedBy(item).version}` : null" class="d-block">
              <b-button
                size="sm"
                variant="outline-success"
                class="w-100"
                :disabled="busy || !!replacedBy(item)"
                @click="reactivate(item)"
              >
                Reactivate
              </b-button>
            </span>
            <b-button
              v-if="canEdit(item)"
              v-b-tooltip.hover
              title="It hasn't checked a BDX file yet, so it can be changed directly."
              size="sm"
              variant="outline-primary"
              :disabled="busy"
              @click="openBuilder('edit', item)"
            >
              Edit
            </b-button>
            <b-button
              v-else-if="item.active"
              v-b-tooltip.hover
              title="It has checked BDX files, so a change makes a new version and switches this one off."
              size="sm"
              variant="outline-primary"
              :disabled="busy"
              @click="openBuilder('version', item)"
            >
              New version
            </b-button>
          </template>
          <b-button size="sm" variant="link" class="px-1" @click="openHistory(item)">History</b-button>
        </div>
      </template>
    </b-table>

    <b-modal v-model="historyOpen" :title="historyTitle" size="lg" hide-footer>
      <div v-if="historyLoading" class="py-3 text-center">
        <cv-spinner class="mr-2" />
        Loading history...
      </div>
      <b-alert v-else-if="historyError" show variant="danger">{{ historyError }}</b-alert>
      <p v-else-if="!history.length" class="text-muted mb-0">No changes have been recorded for this rule yet.</p>
      <ul v-else class="cv-history">
        <li v-for="(event, index) in history" :key="index" class="cv-history__event">
          <div>
            <span class="font-weight-bold">{{ actionLabel(event) }}</span>
            <span v-if="historyRule && historyRule.type === 'custom' && event.version" class="text-muted">
              (version {{ event.version }})
            </span>
          </div>
          <div class="text-muted small">{{ formatWhen(event.occurred_at) }} by {{ event.actor }}</div>
          <div v-if="changeText(event)" class="small">{{ changeText(event) }}</div>
          <div v-if="event.drafted" class="small">Converted from a sentence</div>
          <div v-if="event.reason" class="small">Reason: {{ event.reason }}</div>
        </li>
      </ul>
    </b-modal>

    <custom-rule-builder
      v-if="canManage"
      v-model="builderOpen"
      :mode="builderMode"
      :rule="builderRule"
      :company="company"
      @saved="onBuilderSaved"
    />
  </div>
</template>

<script>
import CustomValidationsService from '@services/customValidationsService'
import { columnLabel, criteriaText } from '@util/criteriaText'
import CustomRuleBuilder from './CustomRuleBuilder.vue'
import CvSpinner from './CvSpinner.vue'

// CV-4 (LIQ-2271): a LimeIQ default rule's severity can be raised per company - Q-A was answered yes
// (raise only, and a company can undo its own raise). The ticket asks for it behind a switch.
const ALLOW_DEFAULT_SEVERITY_CHANGE = true

const APPLIES_NEXT_RUN = 'Changes apply from the next validation run.'

const SEVERITY_LABELS = { critical: 'Critical', non_critical: 'Non-Critical' }

const ACTION_LABELS = {
  create: 'Created',
  edit: 'Changed',
  rename: 'Name or description corrected',
  deactivate: 'Deactivated',
  reactivate: 'Switched back on',
  supersede: 'Replaced by a new version',
  override: 'Severity changed'
}

// What an edit's history entry lists as changed.
const FIELD_LABELS = {
  name: 'name',
  description: 'description',
  severity: 'severity',
  columns: 'columns',
  criteria: 'criteria',
  applicable_keys: 'programs',
  programs_mode: 'programs',
  program_umrs: 'programs',
  program_key_exceptions: 'programs',
  jurisdictions_mode: 'jurisdictions',
  jurisdictions: 'jurisdictions',
  tpas_mode: 'TPAs',
  tpas: 'TPAs',
  scope_overridden: 'scope override',
  effective_from: 'start date',
  effective_to: 'end date'
}

const norm = (name) => String(name).trim().toLowerCase()

// "Only B1306AB2401A" / "All except 3 UMRs" - one part of a rule's scope (LIQ-2716).
const scopePart = (mode, names, word) => {
  const which = names.length === 1 ? names[0] : names.length <= 3 ? names.join(', ') : `${names.length} ${word}s`
  return mode === 'except' ? `All except ${which}` : `Only ${which}`
}

const errorMessage = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.detail) || fallback

export default {
  name: 'CustomValidationRulesTable',

  components: { CustomRuleBuilder, CvSpinner },

  props: {
    company: {
      type: Object,
      required: true
    },
    // Every default rule plus the company's own custom rules (GET .../custom-validations).
    rules: {
      type: Array,
      required: true
    },
    // The jurisdictions of the programs the company leads, plus any its rules name.
    jurisdictions: {
      type: Array,
      default: () => []
    },
    canManage: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      builderOpen: false,
      builderMode: 'create',
      builderRule: null,
      jurisdiction: null,
      typeFilter: 'all',
      statusFilter: 'all',
      hideInactive: false,
      columnTypes: {},
      busy: false,
      severityResets: 0,
      historyOpen: false,
      historyRule: null,
      historyLoading: false,
      historyError: null,
      history: [],
      typeOptions: [
        { value: 'all', text: 'All types' },
        { value: 'default', text: 'Default' },
        { value: 'custom', text: 'Custom' }
      ],
      statusOptions: [
        { value: 'all', text: 'All statuses' },
        { value: 'critical', text: 'Critical' },
        { value: 'non_critical', text: 'Non-Critical' }
      ],
      defaultSeverityOptions: [
        { value: 'non_critical', text: 'Non-Critical (default)' },
        { value: 'critical', text: 'Critical' }
      ],
      // Type, Status, Scope and Actions take only the width their content needs (cv-rules__fit) -
      // Scope is "All" for most rules; the rest of the width goes to the text columns, most to Criteria.
      fields: [
        { key: 'type', label: 'Type', sortable: true, thClass: 'cv-rules__fit', tdClass: 'cv-rules__fit' },
        { key: 'name', label: 'Rule', sortable: true, thStyle: { width: '15%' } },
        { key: 'description', label: 'Description', thStyle: { width: '20%' } },
        // Sorts Critical before Non-Critical, then active before inactive.
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          sortByFormatted: true,
          formatter: (_value, _key, rule) => `${rule.severity === 'critical' ? 0 : 1}${rule.active ? 0 : 1}`,
          thClass: 'cv-rules__fit',
          tdClass: 'cv-rules__fit'
        },
        { key: 'columns', label: 'Columns', thStyle: { width: '15%' } },
        { key: 'criteria', label: 'Criteria', thStyle: { width: '30%' } },
        { key: 'scope', label: 'Scope', thClass: 'cv-rules__fit', tdClass: 'cv-rules__fit' },
        { key: 'actions', label: 'Actions', thClass: 'cv-rules__fit', tdClass: 'cv-rules__fit' }
      ]
    }
  },

  computed: {
    jurisdictionOptions() {
      return [
        { value: null, text: 'All jurisdictions' },
        ...this.jurisdictions.map((name) => ({ value: name, text: name }))
      ]
    },
    visibleRules() {
      return this.rules.filter(
        (rule) =>
          this.inJurisdiction(rule) &&
          (this.typeFilter === 'all' || rule.type === this.typeFilter) &&
          (this.statusFilter === 'all' || rule.severity === this.statusFilter) &&
          (!this.hideInactive || rule.active)
      )
    },
    summary() {
      const defaults = this.visibleRules.filter((rule) => rule.type === 'default').length
      const custom = this.visibleRules.filter((rule) => rule.type === 'custom')
      const inactive = custom.filter((rule) => !rule.active).length
      return (
        `${defaults} LimeIQ default rules and ${custom.length} custom rules` +
        (inactive ? ` (${inactive} inactive)` : '') +
        (this.jurisdiction ? ` apply in ${this.jurisdiction}.` : '.')
      )
    },
    historyTitle() {
      return this.historyRule ? `History - ${this.historyRule.name}` : 'History'
    }
  },

  mounted() {
    this.loadColumnTypes()
  },

  methods: {
    // Column types let the criteria read "before"/"after" for dates. Without them the sentences
    // still show, so a failure here is ignored.
    async loadColumnTypes() {
      try {
        const { data } = await CustomValidationsService.getRuleColumns()
        this.columnTypes = Object.fromEntries((data.columns || []).map((column) => [column.name, column.data_type]))
      } catch (err) {
        this.columnTypes = {}
      }
    },

    // A rule scoped to all jurisdictions shows under every jurisdiction; "all except" shows under every
    // jurisdiction it doesn't leave out.
    inJurisdiction(rule) {
      if (!this.jurisdiction || !rule.jurisdictions || !rule.jurisdictions.length) return true
      const named = rule.jurisdictions.map(norm).includes(norm(this.jurisdiction))
      return rule.jurisdictions_mode === 'except' ? !named : named
    },

    rowClass(rule) {
      return rule && !rule.active ? 'cv-rules__row--inactive' : ''
    },

    severityLabel(severity) {
      return SEVERITY_LABELS[severity] || severity
    },

    severityVariant(severity) {
      return severity === 'critical' ? 'danger' : 'warning'
    },

    isRaised(rule) {
      return !!rule.default_severity && rule.severity !== rule.default_severity
    },

    // Only a Non-Critical default can be raised (Q-A: never below its LimeIQ severity).
    canChangeDefaultSeverity(rule) {
      return ALLOW_DEFAULT_SEVERITY_CHANGE && this.canManage && rule.default_severity === 'non_critical'
    },

    // The version that replaced this one, if any - a replaced version can't be switched back on.
    replacedBy(rule) {
      return this.rules.find((other) => other.supersedes_rule_id === rule.id) || null
    },

    // CV-5 (per the ticket): a custom rule that has never checked a BDX file is changed directly;
    // once it has (first_used_at), a change is a new version.
    canEdit(rule) {
      return rule.type === 'custom' && !rule.first_used_at && !this.replacedBy(rule)
    },

    openBuilder(mode, rule = null) {
      this.builderMode = mode
      this.builderRule = rule
      this.builderOpen = true
    },

    onBuilderSaved({ rule, mode }) {
      const messages = {
        create: rule.active
          ? `"${rule.name}" added.`
          : `"${rule.name}" added, switched off - switch it on from this table when it's ready.`,
        edit: `"${rule.name}" changed.`,
        version: `Version ${rule.version} of "${rule.name}" saved; version ${rule.version - 1} is switched off.`
      }
      this.toastSaved(messages[mode] || 'Rule saved.')
      this.$emit('changed')
    },

    columnsText(rule) {
      return rule.columns && rule.columns.length ? rule.columns.map(columnLabel).join('; ') : '—'
    },

    criteriaOf(rule) {
      return criteriaText(rule.criteria, { columnTypes: this.columnTypes }) || rule.criteria_text || '—'
    },

    // "All", or a short line for each part of the scope that's narrowed (LIQ-2716) - keeps the Scope
    // column narrow. A rule saved before LIQ-2716 may name its keys (applicable_keys).
    scopeLines(rule) {
      const lines = []
      const keys = rule.applicable_keys || []
      const umrs = rule.program_umrs || []
      if (!rule.programs_mode && keys.length) lines.push(`Programs: ${keys.length} key${keys.length === 1 ? '' : 's'}`)
      else if (rule.programs_mode && rule.programs_mode !== 'all' && umrs.length) {
        lines.push(`Programs: ${scopePart(rule.programs_mode, umrs, 'UMR')}`)
      }
      const jurisdictions = rule.jurisdictions || []
      if (jurisdictions.length) lines.push(`Jurisdictions: ${scopePart(rule.jurisdictions_mode, jurisdictions, 'jurisdiction')}`)
      const tpas = rule.tpas || []
      if (tpas.length) lines.push(`TPAs: ${scopePart(rule.tpas_mode, tpas, 'TPA')}`)
      if (rule.scope_overridden) lines.push('Saved over a scope warning')
      return lines.length ? lines : ['All']
    },

    // The whole scope, on hover: every UMR, key, jurisdiction and TPA, and any warning it was saved over.
    scopeTitle(rule) {
      const parts = []
      if (rule.applicable_keys && rule.applicable_keys.length) parts.push(`Keys: ${rule.applicable_keys.join(', ')}`)
      if (rule.program_umrs && rule.program_umrs.length) parts.push(`UMRs: ${rule.program_umrs.join(', ')}`)
      if (rule.program_key_exceptions && rule.program_key_exceptions.length) {
        const how = rule.programs_mode === 'except' ? 'kept in' : 'left out'
        parts.push(`Keys ${how}: ${rule.program_key_exceptions.join(', ')}`)
      }
      if (rule.jurisdictions && rule.jurisdictions.length) parts.push(`Jurisdictions: ${rule.jurisdictions.join(', ')}`)
      if (rule.tpas && rule.tpas.length) parts.push(`TPAs: ${rule.tpas.join(', ')}`)
      ;(rule.scope_warnings || []).forEach((warning) => parts.push(`Warning: ${warning}`))
      return parts.length ? parts.join('\n') : null
    },

    toastSaved(message) {
      this.$bvToast.toast(`${message} ${APPLIES_NEXT_RUN}`, { title: 'Saved', variant: 'success', solid: true })
    },

    // Runs a row action inside a confirmation dialog, showing any refusal there.
    async confirmAction({ title, text, confirmButtonText, input, run, done }) {
      await this.$swal({
        title,
        text,
        input,
        inputPlaceholder: input ? 'Reason (optional)' : undefined,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText: 'CANCEL',
        showLoaderOnConfirm: true,
        allowOutsideClick: false,
        preConfirm: async (value) => {
          if (value === false) return false
          this.busy = true
          try {
            await run(value)
          } catch (err) {
            this.$swal.showValidationMessage(errorMessage(err, 'The change could not be saved.'))
            return false
          } finally {
            this.busy = false
          }
          done()
          this.$emit('changed')
        }
      })
    },

    deactivate(rule) {
      this.confirmAction({
        title: `Deactivate "${rule.name}"?`,
        text: 'It stops applying from the next validation run. Results already recorded are kept, and the rule stays listed.',
        confirmButtonText: 'DEACTIVATE',
        input: 'text',
        run: (reason) => CustomValidationsService.deactivateRule(this.company.id, rule.id, reason),
        done: () => this.toastSaved(`"${rule.name}" deactivated.`)
      })
    },

    reactivate(rule) {
      this.confirmAction({
        title: `Switch "${rule.name}" back on?`,
        text: 'It applies again from the next validation run.',
        confirmButtonText: 'REACTIVATE',
        run: () => CustomValidationsService.reactivateRule(this.company.id, rule.id),
        done: () => this.toastSaved(`"${rule.name}" switched back on.`)
      })
    },

    async changeDefaultSeverity(rule, severity) {
      const raising = severity !== rule.default_severity
      await this.confirmAction({
        title: raising
          ? `Make "${rule.name}" ${this.severityLabel(severity)} for ${this.company.name}?`
          : `Set "${rule.name}" back to ${this.severityLabel(severity)}?`,
        text: raising
          ? `${this.company.name}'s programs will treat it as ${this.severityLabel(severity)} from the next validation run. Results already recorded keep the severity they had.`
          : `This removes ${this.company.name}'s raise. Results already recorded keep the severity they had.`,
        confirmButtonText: 'CHANGE',
        run: () => CustomValidationsService.setDefaultSeverity(this.company.id, rule.id, severity),
        done: () => this.toastSaved(`"${rule.name}" is now ${this.severityLabel(severity)}.`)
      })
      // Cancelled or refused: put the dropdown back to the saved severity.
      this.severityResets += 1
    },

    async openHistory(rule) {
      this.historyRule = rule
      this.historyOpen = true
      this.historyLoading = true
      this.historyError = null
      this.history = []
      try {
        const { data } = await CustomValidationsService.getRuleHistory(this.company.id, rule.id)
        this.history = data.events || []
      } catch (err) {
        this.historyError = errorMessage(err, 'Failed to load the history.')
      } finally {
        this.historyLoading = false
      }
    },

    actionLabel(event) {
      return ACTION_LABELS[event.action] || event.action
    },

    changeText(event) {
      const before = (event.changes && event.changes.before) || {}
      const after = (event.changes && event.changes.after) || {}
      if (event.action === 'override') {
        return `${this.severityLabel(before.severity)} → ${this.severityLabel(after.severity)}`
      }
      if (event.action === 'rename') {
        const parts = []
        if (before.name !== after.name) parts.push(`Name: "${before.name}" → "${after.name}"`)
        if (before.description !== after.description) parts.push('Description changed')
        return parts.join('. ')
      }
      if (event.action === 'edit') {
        const parts = Object.keys(after).map((field) => {
          if (field === 'severity') return `severity ${this.severityLabel(before.severity)} → ${this.severityLabel(after.severity)}`
          if (field === 'name') return `name "${before.name}" → "${after.name}"`
          return FIELD_LABELS[field] || field
        })
        return parts.length ? `Changed: ${[...new Set(parts)].join(', ')}` : ''
      }
      return ''
    },

    // The API sends UTC times without a zone.
    formatWhen(value) {
      if (!value) return ''
      const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`)
      return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style>
.cv-rules {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px 20px;
}

.cv-rules__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.cv-rules__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

/* Room on the right for the dropdown arrow, so it never sits on the text. */
.cv-rules__select {
  width: auto;
  min-width: 150px;
  padding-right: 2rem;
}

.cv-rules__severity {
  min-width: 190px;
  padding-right: 2rem;
}

.cv-rules__switch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cv-rules__switch-label {
  cursor: pointer;
  user-select: none;
}

/* The theme keeps every table cell on one line (white-space: nowrap), so long descriptions and
   criteria stretched this table past the page and pushed the actions out of sight. Text wraps here
   instead, so every column fits without scrolling sideways. */
.cv-rules__table th,
.cv-rules__table td {
  white-space: normal;
  overflow-wrap: break-word;
}

.cv-rules__table td {
  vertical-align: top;
  font-size: 0.85rem;
}

/* As narrow as the content allows: badges, the severity dropdown, the action buttons. */
.cv-rules__table .cv-rules__fit {
  width: 1%;
}

.cv-rules__row--inactive td {
  color: #8a8f98;
  background: #fafafa;
}

/* One button per line keeps the Actions column narrow. */
.cv-rules__actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.cv-rules__actions .btn {
  white-space: nowrap;
}

.cv-history {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

.cv-history__event {
  padding: 8px 0;
  border-bottom: 1px solid #eef0f2;
}

.cv-history__event:last-child {
  border-bottom: 0;
}
</style>
